// Campo osservativo della variazione termica. Il colore è una stima locale:
// niente extrapolazione nelle zone senza almeno due stazioni vicine.
import {
    DOMAIN, DOMAIN_COORDS, domainPoint, loadSicilyFeature,
    compositeSicilyMask, refreshCanvasSource,
} from './interpol.js';

const SOURCE = 'ws-anl-tempvar-field-src';
const LAYER = 'ws-anl-tempvar-field';
const CELL = 12;
const SIGMA = 95;
const CUTOFF = 230;
const NEARBY = 110;
const MAX_NEAREST = 95;

let canvas = null;
let generation = 0;
let frame = 0;
let pendingResolve = null;

export function prepareVariationSamples(rows) {
    const samples = rows.map(row => {
        if (row.lat == null || row.lng == null || row.delta == null) return null;
        const lat = Number(row.lat), lng = Number(row.lng), value = Number(row.delta);
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(value)
            || lat < DOMAIN.south || lat > DOMAIN.north || lng < DOMAIN.west || lng > DOMAIN.east) return null;
        const point = domainPoint(lat, lng);
        return { x: point.x, y: point.y, value };
    }).filter(Boolean);
    const unique = new Map();
    for (const sample of samples) {
        const key = `${Math.round(sample.x)},${Math.round(sample.y)}`;
        const entry = unique.get(key) || { ...sample, sum: 0, count: 0 };
        entry.sum += sample.value;
        entry.count += 1;
        unique.set(key, entry);
    }
    return [...unique.values()].map(sample => ({ x: sample.x, y: sample.y, value: sample.sum / sample.count }));
}

export function buildVariationIndex(samples) {
    const index = new Map();
    for (const sample of samples) {
        const key = `${Math.floor(sample.x / CUTOFF)},${Math.floor(sample.y / CUTOFF)}`;
        if (!index.has(key)) index.set(key, []);
        index.get(key).push(sample);
    }
    return index;
}

// Exported for deterministic coverage checks: isolated stations do not paint
// a regional field, but their measured value remains available at close zoom.
export function estimateVariationAt(index, x, y) {
    const bx = Math.floor(x / CUTOFF), by = Math.floor(y / CUTOFF);
    let sum = 0, weightSum = 0, nearby = 0, nearest2 = Infinity;
    for (let gx = bx - 1; gx <= bx + 1; gx += 1) {
        for (let gy = by - 1; gy <= by + 1; gy += 1) {
            for (const sample of index.get(`${gx},${gy}`) || []) {
                const dx = x - sample.x, dy = y - sample.y, d2 = dx * dx + dy * dy;
                if (d2 > CUTOFF * CUTOFF) continue;
                if (d2 < nearest2) nearest2 = d2;
                if (d2 <= NEARBY * NEARBY) nearby += 1;
                const weight = Math.exp(-d2 / (2 * SIGMA * SIGMA)) / Math.max(100, d2);
                sum += sample.value * weight;
                weightSum += weight;
            }
        }
    }
    if (nearby < 2 || nearest2 > MAX_NEAREST * MAX_NEAREST || !weightSum) return null;
    const proximity = 1 - Math.sqrt(nearest2) / MAX_NEAREST;
    return { value: sum / weightSum, alpha: Math.min(0.78, 0.32 + proximity * 0.52) };
}

export function ensureTemperatureField(map, beforeLayer) {
    if (!canvas) {
        canvas = document.createElement('canvas');
        canvas.width = DOMAIN.width;
        canvas.height = DOMAIN.height;
    }
    if (!map.getSource(SOURCE)) {
        map.addSource(SOURCE, { type: 'canvas', canvas, coordinates: DOMAIN_COORDS, animate: false });
    }
    if (!map.getLayer(LAYER)) {
        map.addLayer({
            id: LAYER, type: 'raster', source: SOURCE,
            paint: { 'raster-opacity': 0.9, 'raster-fade-duration': 0, 'raster-resampling': 'linear' },
        }, beforeLayer);
    }
}

export function clearTemperatureField(map) {
    generation += 1;
    if (frame) { cancelAnimationFrame(frame); frame = 0; }
    if (pendingResolve) { pendingResolve(false); pendingResolve = null; }
    if (!canvas) return;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    refreshCanvasSource(map, SOURCE);
}

export async function renderTemperatureField(map, rows, colorFor, isCurrent) {
    const current = ++generation;
    if (frame) { cancelAnimationFrame(frame); frame = 0; }
    if (pendingResolve) { pendingResolve(false); pendingResolve = null; }
    const samples = prepareVariationSamples(rows);
    const sicily = await loadSicilyFeature();
    if (current !== generation || !isCurrent()) return false;
    if (!sicily || samples.length < 2) { clearTemperatureField(map); return false; }

    const index = buildVariationIndex(samples);
    const cols = Math.ceil(DOMAIN.width / CELL), rowsCount = Math.ceil(DOMAIN.height / CELL);
    const field = document.createElement('canvas');
    field.width = cols;
    field.height = rowsCount;
    const ctx = field.getContext('2d', { alpha: true });
    let y = 0;
    return new Promise(resolve => {
        pendingResolve = resolve;
        const step = () => {
            if (current !== generation || !isCurrent()) { frame = 0; pendingResolve = null; resolve(false); return; }
            const start = performance.now();
            while (y < rowsCount) {
                for (let x = 0; x < cols; x += 1) {
                    const estimate = estimateVariationAt(index, (x + 0.5) * CELL, (y + 0.5) * CELL);
                    if (!estimate) continue;
                    const rgb = window.chroma(colorFor(estimate.value)).rgb();
                    ctx.fillStyle = `rgba(${Math.round(rgb[0])},${Math.round(rgb[1])},${Math.round(rgb[2])},${estimate.alpha.toFixed(3)})`;
                    ctx.fillRect(x, y, 1, 1);
                }
                y += 1;
                if (performance.now() - start > 10) break;
            }
            if (y < rowsCount) { frame = requestAnimationFrame(step); return; }
            frame = 0;
            const output = canvas.getContext('2d', { alpha: true });
            output.clearRect(0, 0, DOMAIN.width, DOMAIN.height);
            output.imageSmoothingEnabled = true;
            output.imageSmoothingQuality = 'high';
            output.drawImage(field, 0, 0, DOMAIN.width, DOMAIN.height);
            compositeSicilyMask(output, 'destination-in');
            refreshCanvasSource(map, SOURCE);
            pendingResolve = null;
            resolve(true);
        };
        frame = requestAnimationFrame(step);
    });
}
